// src/hooks/useSeo.ts
import { useEffect } from "react";

type SEOParams = {
  title?: string;
  description?: string;
  image?: string | null;
  url?: string;
  keywords?: string[];
  structuredData?: object | null;
  noindex?: boolean;
};

function setMetaName(name: string, content?: string | null) {
  const selector = `meta[name="${name}"]`;
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (content) {
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", name);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  } else if (el) {
    el.remove();
  }
}

function setMetaProperty(property: string, content?: string | null) {
  const selector = `meta[property="${property}"]`;
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (content) {
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("property", property);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  } else if (el) {
    el.remove();
  }
}

export function useSeo({
  title,
  description,
  image,
  url,
  keywords,
  structuredData,
  noindex,
}: SEOParams) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    setMetaName("description", description || null);
    setMetaName(
      "keywords",
      keywords && keywords.length ? keywords.join(", ") : null,
    );

    setMetaProperty("og:title", title || null);
    setMetaProperty("og:description", description || null);
    setMetaProperty("og:type", "website");
    setMetaProperty("og:image", image || null);
    setMetaProperty("og:url", url || window.location.href);

    setMetaName("twitter:card", image ? "summary_large_image" : "summary");
    setMetaName("twitter:title", title || null);
    setMetaName("twitter:description", description || null);
    setMetaName("twitter:image", image || null);

    // canonical link
    let canonical = document.head.querySelector(
      'link[rel="canonical"]',
    ) as HTMLLinkElement | null;
    const canonicalHref = url || window.location.href;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalHref);

    // robots / noindex
    if (noindex) {
      setMetaName("robots", "noindex, nofollow");
    } else {
      const robots = document.head.querySelector(
        'meta[name="robots"]',
      ) as HTMLMetaElement | null;
      if (robots) robots.remove();
    }

    // Structured data (JSON-LD)
    const ldId = "ld-json-seo";
    let ldScript = document.getElementById(ldId) as HTMLScriptElement | null;
    if (structuredData) {
      if (!ldScript) {
        ldScript = document.createElement("script");
        ldScript.type = "application/ld+json";
        ldScript.id = ldId;
        document.head.appendChild(ldScript);
      }
      ldScript.textContent = JSON.stringify(structuredData);
    } else if (ldScript) {
      ldScript.remove();
    }

    return () => {
      // restore title
      document.title = prevTitle;
      // remove structured data we added
      const ld = document.getElementById(ldId);
      if (ld) ld.remove();
    };
  }, [
    title,
    description,
    image,
    url,
    keywords?.join(","),
    JSON.stringify(structuredData || {}),
    noindex,
  ]);
}
